<template>
  <!-- 预览图 -->
  <AttrGroup title="小部件边框图">
    <template #title>
      <div class="flex justify-between w-full">
        <div>
          小部件边框图
          <a-tooltip
            :title="
              '边框图必须上传，当小部件尺寸一致时建议统一边框,' +
              '边框图尺寸=正常尺寸*' +
              scale
            "
          >
            <icon :icon="'help.svg'" :width="20" :height="20" fill="none" />
          </a-tooltip>
        </div>
        <div>
          <span>统一边框：</span>
          <a-switch
            :checked="sameEditBox"
            :disabled="!!attrDisabled || disabled"
            @change="onSameChange"
          />
        </div>
      </div>
    </template>

    <!-- 缩放提示 -->
    <!-- <SlotPreviewScaleDesc :image-type="'边框图'" /> -->

    <!-- 容器列表 -->
    <template
      v-for="(item, index) in widgets.filter((item) => item.value != 'none')"
      :key="(item.label ?? 'unknown') + index"
    >
      <AttrImage
        :disabled="disabled"
        :label="item.label ?? ''"
        class="mb-2.5"
        :base64="getWidgetEditboxImage(item.value)"
        @change="(image: string) => onImageUpdate(item.value, image)"
        @delete="() => onImageDelete(item.value)"
      />
    </template>
  </AttrGroup>
</template>
<script lang="ts" setup>
import { useSlotFollow } from '@/hooks/use-slot-follow';
import { Shape } from '@/services/shape';
import { useEditorStore } from '@/store/use-editor-store';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { computed, PropType } from 'vue';

const editorStore = useEditorStore();
const { followParentAttrs } = useSlotFollow();

const props = defineProps({
  shape: {
    type: Object as PropType<Shape>,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const watchfaceStore = useWatchfaceStore();

// 获取缩放值
const scale = computed(() => {
  const deviceType = watchfaceStore.deviceType;
  // O61、Q63、Q66 边框图尺寸为正常尺寸100%
  if (['O61', 'Q63', 'Q66'].includes(deviceType)) {
    return '100%';
  }
  const scaleValue = watchfaceStore.watchfaceDevice?.editScale ?? 1;
  return `${Math.floor(scaleValue * 100)}%`;
});

// 子容器跟随父容器时，使用父容器的 sameEditBox 状态
const effectiveSameEditBox = computed(() => {
  const shape = props.shape;
  if (shape.attrs.slotFollowingId) {
    const parentShape = Shape.getShape(shape.attrs.slotFollowingId);
    return !!parentShape?.attrs.sameEditBox;
  }
  return !!shape.attrs.sameEditBox;
});

const widgets = computed(() => {
  const shape = props.shape;
  const sameEditBox = effectiveSameEditBox.value;
  if (sameEditBox) {
    return [
      {
        label: '容器边框',
        value: shape.id
      }
    ];
  } else {
    const res = shape.children.map((item) => {
      return {
        label: item.attrs.name,
        value: item.id
      };
    });
    if (shape.attrs.includeEmpty) {
      res.push({
        value: 'none',
        label: '无'
      });
    }
    res.reverse();
    return res;
  }
});

// 小部件预览图
const attrDisabled = computed(() => {
  return editorStore.attrDisabled || !canUse('slot-border-image').valid;
});
const getWidgetEditboxImage = (widgetId: string) => {
  const shape = Shape.getShape(widgetId);
  return shape?.getResources({
    resourceName: 'editBox',
    colorGroup: editorStore.themeColor?.id ?? '',
    base64: true
  })?.[0];
};

const onImageUpdate = async (widgetId: string, image: string) => {
  const shape = Shape.getShape(widgetId);
  shape?.uploadResource({
    resourceName: 'editBox',
    colorGroup: editorStore.themeColor?.id ?? '',
    realpath: image,
    dirname: '_widget'
  });
  // 容器继承时，需同步上传到子容器。统一边框关闭时，用 slot 和 widgetIndex 传播到子容器对应小部件
  if (props.shape.attrs.followingGroupName !== undefined) {
    const widgetIndex = props.shape.attrs.sameEditBox
      ? undefined
      : props.shape.children.findIndex((c) => c.id === widgetId);
    followParentAttrs(
      props.shape,
      'uploadEditbox',
      image,
      undefined,
      widgetIndex ?? -1
    );
  }
};
const onImageDelete = async (widgetId: string) => {
  const shape = Shape.getShape(widgetId);
  shape?.deleteResource({
    resourceName: 'editBox',
    colorGroup: editorStore.themeColor?.id ?? ''
  });
  // 容器继承时，需同步删除子容器中对应的边框图。统一边框关闭时，用 slot 和 widgetIndex 传播到子容器对应小部件
  if (props.shape.attrs.followingGroupName !== undefined) {
    const widgetIndex = props.shape.attrs.sameEditBox
      ? undefined
      : props.shape.children.findIndex((c) => c.id === widgetId);
    followParentAttrs(
      props.shape,
      'deleteEditbox',
      'editBox',
      undefined,
      widgetIndex ?? -1
    );
  }
};

const sameEditBox = effectiveSameEditBox;
const onSameChange = (val: boolean) => {
  props.shape.setAttrs({ sameEditBox: !!val });
  if (props.shape.attrs.followingGroupName !== undefined)
    followParentAttrs(props.shape, 'sameEditBox', !!val);
};
</script>
<style scoped lang="less"></style>
